import {
  DEFAULT_THEME_ID,
  getGameRules,
  type EditionKeys,
  type GameRules,
  type PoliticalCharacter,
  type ThemeId,
  type ThemeStatus,
  type ThemeSummary,
  type ThemeText,
} from '@secretnenshi/shared';
import generated from './generated/themes.json';

/*
 * Game variants. The source of truth is content/themes/<id>/; `npm run
 * content` validates it and regenerates ./generated/themes.json and the web
 * portraits. Do not edit the JSON.
 */

export interface Theme {
  id: ThemeId;
  status: ThemeStatus;
  validAsOf: string;
  /** What this edition's character files call the mechanical slots. */
  keys: EditionKeys;
  text: ThemeText;
  characters: readonly PoliticalCharacter[];
  /** Mechanical overrides; merged over the standard rules. */
  rules: Partial<GameRules>;
}

const THEMES: Record<ThemeId, Theme> = generated as unknown as Record<ThemeId, Theme>;

export function isThemeId(id: unknown): id is ThemeId {
  return typeof id === 'string' && Object.hasOwn(THEMES, id);
}

/** Falls back to the default theme for unknown ids (e.g. a theme removed after a game started). */
export function getTheme(id: ThemeId | null | undefined): Theme {
  return (id && THEMES[id]) || THEMES[DEFAULT_THEME_ID]!;
}

/** Menu order: released themes first, then previews; default theme first. */
export function listThemes(): ThemeSummary[] {
  return Object.values(THEMES)
    .sort((a, b) => (a.status === b.status ? (a.id === DEFAULT_THEME_ID ? -1 : b.id === DEFAULT_THEME_ID ? 1 : a.id.localeCompare(b.id)) : a.status === 'released' ? -1 : 1))
    .map((t) => ({
      id: t.id,
      title: t.text.title,
      editionName: t.text.editionName,
      tagline: t.text.tagline,
      menuDescription: t.text.menuDescription,
      status: t.status,
    }));
}

/** Standard rules for the table size with the theme's overrides applied. */
export function getThemeRules(themeId: ThemeId, playerCount: number): GameRules {
  return { ...getGameRules(playerCount), ...getTheme(themeId).rules };
}
